/**
 * RenameTenantModal — the one way to rename an organization.
 *
 * Two steps, so it cannot happen by a stray click:
 *   1. type the new name; the server checks it (taken, invalid) as you type;
 *   2. type the CURRENT name again; only then does the red "Rename" button
 *      wake up. This step also says what else changes with the name — the
 *      Library folder, the brand texts that name the organization (here and
 *      in the organizations under it), test-conversation titles — and what is
 *      kept as it is (a text too long for its field with the new name; the app
 *      name).
 * The server checks the typed name too (POST /group-admin/tenants/<id>/rename),
 * so no client can skip the confirmation. After saving, a last step lists what
 * actually changed.
 */

import { useEffect, useRef, useState } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { groupAdminService } from '@modules/group_admin/services/groupAdminService';
import type { TenantRenameChanges, TenantRenamePreview, TenantRenameResult } from '@modules/group_admin/types';
import './RenameTenantModal.css';

interface RenameTenantModalProps {
  tenantId: string;
  currentName: string;
  onClose: () => void;
  /** Fired once the rename is saved, with the stored new name (the modal stays
   *  open on its last step, listing what changed, until it is closed). */
  onRenamed: (newName: string) => void;
}

const PREVIEW_DELAY_MS = 350;

const KNOWN_ERRORS = new Set([
  'ACCESS_DENIED', 'NOT_FOUND', 'NAME_REQUIRED', 'NAME_TOO_LONG', 'NAME_INVALID',
  'NAME_UNCHANGED', 'NAME_TAKEN', 'CONFIRMATION_MISMATCH', 'RENAME_CONFLICT', 'RENAME_FAILED',
]);

/** A brand field as the White-Label editor names it. */
const FIELD_LABEL_KEYS: Record<string, string> = {
  name: 'whiteLabel.brandName',
  companyName: 'whiteLabel.companyName',
  tagline: 'whiteLabel.tagline',
  pageTitle: 'whiteLabel.pageTitle',
  loginEyebrow: 'whiteLabel.eyebrow',
  'terminology.greeting': 'whiteLabel.greeting',
  'terminology.greetingQuestion': 'whiteLabel.greetingSubLine',
  'email.brandName': 'whiteLabel.productNameInEmails',
  'email.senderName': 'whiteLabel.senderName',
  'email.subjectReset': 'whiteLabel.resetEmailSubject',
  'email.subjectInvite': 'whiteLabel.invitationEmailSubject',
  'email.footerText': 'whiteLabel.footerText',
  appName: 'whiteLabel.appName',
};

/** As the server stores a name and compares the confirmation. */
const tidy = (s: string) => s.trim().split(/\s+/).join(' ');

function errorCode(err: unknown): string {
  const data = (err as { response?: { data?: { error_code?: string } } })?.response?.data;
  return data?.error_code || 'RENAME_FAILED';
}

function hasChanges(changes: TenantRenameChanges | undefined): boolean {
  if (!changes) return false;
  return !!changes.library_folder || changes.eval_conversations > 0 || changes.eval_conversations_kept > 0
    || changes.brand_fields.some(b => b.fields.length > 0 || b.kept.length > 0);
}

/** What changes (or changed) with the name, and what is kept as it is. */
function ChangesList({ changes, heading }: { changes: TenantRenameChanges; heading: string }) {
  const { t, direction } = useLanguage();
  const arrow = direction === 'rtl' ? '←' : '→';
  const label = (field: string) =>
    t(field.startsWith('loginFeatures.') ? 'whiteLabel.featureTicks' : (FIELD_LABEL_KEYS[field] ?? field));
  // One line per organization: its texts, by the White-Label editor's names.
  const updated = changes.brand_fields.filter(b => b.fields.length > 0);
  const kept = changes.brand_fields.flatMap(b => b.kept.map(k => ({ org: b.tenant_name, ...k })));
  const changing = !!changes.library_folder || updated.length > 0 || changes.eval_conversations > 0;

  return (
    <div className="rename-tenant-changes" data-testid="rename-tenant-changes">
      {changing && (
        <>
          <p className="rename-tenant-changes-heading">{heading}</p>
          <ul>
            {changes.library_folder && (
              <li>
                {t('groupAdmin.rename.libraryFolder')}: <bdi>{changes.library_folder.from}</bdi> {arrow}{' '}
                <bdi>{changes.library_folder.to}</bdi>
              </li>
            )}
            {updated.map(b => (
              <li key={`u-${b.tenant_id}`}>
                <bdi dir="auto">{b.tenant_name}</bdi>: {[...new Set(b.fields.map(label))].join(t('groupAdmin.rename.listSeparator'))}
              </li>
            ))}
            {changes.eval_conversations > 0 && (
              <li>{t('groupAdmin.rename.evalTitles', { count: changes.eval_conversations })}</li>
            )}
          </ul>
        </>
      )}
      {(kept.length > 0 || changes.eval_conversations_kept > 0) && (
        <>
          <p className="rename-tenant-changes-heading">{t('groupAdmin.rename.keptHeading')}</p>
          <ul>
            {kept.map(({ org, field, reason, limit }) => (
              <li key={`k-${org}-${field}`}>
                <bdi>{org}</bdi> — {label(field)}:{' '}
                {reason === 'kept_app_name'
                  ? t('groupAdmin.rename.keptAppName')
                  : t('groupAdmin.rename.keptTooLong', { limit })}
              </li>
            ))}
            {changes.eval_conversations_kept > 0 && (
              <li>{t('groupAdmin.rename.evalTitlesKept', { count: changes.eval_conversations_kept })}</li>
            )}
          </ul>
        </>
      )}
    </div>
  );
}

export default function RenameTenantModal({ tenantId, currentName, onClose, onRenamed }: RenameTenantModalProps) {
  const { t, direction } = useLanguage();
  // Points from the old name to the new one in the reading direction.
  const arrow = direction === 'rtl' ? '←' : '→';
  const [step, setStep] = useState<'name' | 'confirm' | 'done'>('name');
  const [newName, setNewName] = useState('');
  const [preview, setPreview] = useState<TenantRenamePreview | null>(null);
  const [checking, setChecking] = useState(false);
  const [typed, setTyped] = useState('');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<TenantRenameResult | null>(null);
  const [error, setError] = useState('');
  const latest = useRef(0);

  const errorText = (code: string) =>
    t(`groupAdmin.rename.errors.${KNOWN_ERRORS.has(code) ? code : 'RENAME_FAILED'}`);

  // Ask the server what this name would change, a moment after typing stops.
  useEffect(() => {
    setError('');
    if (!newName.trim()) {
      // Retire any check still in flight: its answer is for text that is gone.
      latest.current += 1;
      setPreview(null);
      setChecking(false);
      return;
    }
    const ticket = ++latest.current;
    setChecking(true);
    const timer = window.setTimeout(() => {
      groupAdminService.previewTenantRename(tenantId, newName)
        .then(res => { if (ticket === latest.current) setPreview(res); })
        .catch(err => { if (ticket === latest.current) { setPreview(null); setError(errorText(errorCode(err))); } })
        .finally(() => { if (ticket === latest.current) setChecking(false); });
    }, PREVIEW_DELAY_MS);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newName, tenantId]);

  const usable = !!preview && !preview.error && !checking;
  const confirmed = tidy(typed) === tidy(currentName);

  const submit = async () => {
    if (!usable || !confirmed || saving) return;
    setSaving(true);
    setError('');
    try {
      const saved = await groupAdminService.renameTenant(tenantId, newName, typed);
      setResult(saved);
      setStep('done');
      setSaving(false);
      onRenamed(saved.new_name);
    } catch (err) {
      setError(errorText(errorCode(err)));
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={saving ? undefined : onClose}>
      <div className="modal rename-tenant-modal" role="dialog" aria-modal="true"
           aria-labelledby="rename-tenant-title" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title" id="rename-tenant-title">{t('groupAdmin.rename.title')}</h3>
          <button className="close-btn" onClick={onClose} disabled={saving} aria-label={t('common.close')}>×</button>
        </div>

        <div className="modal-content">
          {step === 'name' && (
            <>
              <div className="form-group">
                <span className="rename-tenant-label">{t('groupAdmin.rename.currentName')}</span>
                <div className="rename-tenant-current"><bdi>{currentName}</bdi></div>
              </div>
              <div className="form-group">
                <label htmlFor="rename-tenant-new">{t('groupAdmin.rename.newName')}</label>
                <input
                  id="rename-tenant-new"
                  dir="auto"
                  className="input"
                  value={newName}
                  maxLength={200}
                  autoFocus
                  onChange={e => setNewName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && usable) setStep('confirm'); }}
                  placeholder={t('groupAdmin.rename.newNamePlaceholder')}
                />
                {preview?.error && (
                  <p className="rename-tenant-field-error" role="alert">{errorText(preview.error)}</p>
                )}
              </div>

              {usable && (
                <p className="rename-tenant-note" data-testid="rename-tenant-note">
                  {t('groupAdmin.rename.appearsEverywhere')}
                </p>
              )}
            </>
          )}

          {step === 'confirm' && preview && (
            <>
              <div className="rename-tenant-warning" role="alert">
                <p className="rename-tenant-summary">
                  <bdi>{currentName}</bdi> {arrow} <bdi>{preview.new_name}</bdi>
                </p>
                <p>{t('groupAdmin.rename.warning')}</p>
              </div>
              {hasChanges(preview.changes) && (
                <ChangesList changes={preview.changes!} heading={t('groupAdmin.rename.alsoChanges')} />
              )}
              <div className="form-group">
                <label htmlFor="rename-tenant-confirm">
                  {t('groupAdmin.rename.typeToConfirm')} <bdi className="rename-tenant-strong">{currentName}</bdi>
                </label>
                <input
                  id="rename-tenant-confirm"
                  dir="auto"
                  className="input"
                  value={typed}
                  autoFocus
                  autoComplete="off"
                  onPaste={e => e.preventDefault()}
                  onChange={e => setTyped(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') submit(); }}
                />
              </div>
            </>
          )}

          {step === 'done' && result && (
            <>
              <p className="rename-tenant-done" role="status">
                {t('groupAdmin.rename.done', { name: result.new_name })}
              </p>
              {hasChanges(result.changes) && (
                <ChangesList changes={result.changes} heading={t('groupAdmin.rename.changedWith')} />
              )}
            </>
          )}

          {error && <p className="rename-tenant-field-error" role="alert">{error}</p>}
        </div>

        <div className="modal-actions">
          {step === 'name' && (
            <>
              <button type="button" className="btn-secondary" onClick={onClose}>{t('vault.cancel')}</button>
              <button type="button" className="btn-primary" disabled={!usable} onClick={() => setStep('confirm')}>
                {t('groupAdmin.rename.continue')}
              </button>
            </>
          )}
          {step === 'confirm' && (
            <>
              <button type="button" className="btn-secondary" disabled={saving}
                      onClick={() => { setTyped(''); setError(''); setStep('name'); }}>
                {t('groupAdmin.rename.back')}
              </button>
              <button type="button" className="rename-tenant-danger" disabled={!confirmed || saving} onClick={submit}>
                {saving ? t('groupAdmin.rename.renaming') : t('groupAdmin.rename.confirmButton')}
              </button>
            </>
          )}
          {step === 'done' && (
            <button type="button" className="btn-primary" onClick={onClose}>{t('common.close')}</button>
          )}
        </div>
      </div>
    </div>
  );
}
